<template>
  <component
    :is="tag"
    ref="dropdown"
    class="m-dropdown"
    :class="{ 'm-dropdown--open': open, 'm-dropdown--full-height': fullHeight }"
  >
    <slot
      name="trigger"
      :toggle="toggle"
      :open="open"
      :a11y-props="{
        'aria-controls': contentId,
        'aria-expanded': `${open}`,
        id: toggleId,
      }"
    >
      <MButtonIcon
        :id="toggleId"
        type="button"
        icon="more-vertical"
        size="h3"
        :aria-label="ariaLabel"
        :qa-locator="qaLocator"
        :aria-controls="contentId"
        :aria-expanded="`${open}`"
        @click="toggle"
      />
    </slot>
    <transition name="m-dropdown" mode="out-in">
      <div
        v-if="open"
        :id="contentId"
        class="m-dropdown__transition"
        :aria-labelledby="toggleId"
      >
        <slot :toggle="toggle" :hide="hide" :open="open" name="content" />
      </div>
    </transition>
  </component>
</template>

<script>
import { onClickOutside } from '@vueuse/core'
import { computed, defineComponent, ref, useId } from 'vue'

import useI18n from '../../../composables/useI18n'
import MButtonIcon from '../../controls/ButtonIcon/MButtonIcon'

import sv from './locales/sv.json'

export default defineComponent({
  name: 'MDropdown',
  components: { MButtonIcon },
  i18n: {
    messages: {
      sv,
    },
  },
  props: {
    /** Selector used for system tests */
    qaLocator: {
      type: String,
      default: 'dropdown-toggle',
    },
    /** The HTML element to render */
    tag: {
      type: String,
      default: 'div',
    },
    /** Fill the height of the container. Sets display flex and height 100% */
    fullHeight: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { t } = useI18n()
    const id = useId()
    const open = ref(false)
    const toggleId = ref(`m-dropdown-toggle-${id}`)
    const contentId = ref(`m-dropdown-content-${id}`)
    const ariaLabel = computed(() =>
      open.value
        ? t('MDropdown.close', 'Close menu')
        : t('MDropdown.open', 'Open menu')
    )

    const toggle = () => (open.value = !open.value).value
    const hide = () => (open.value = false)

    const dropdown = ref(null)
    onClickOutside(dropdown, () => {
      hide()
    })

    return { hide, open, toggle, ariaLabel, toggleId, contentId, dropdown }
  },
})
</script>

<style lang="scss" scoped>
.m-dropdown {
  display: inline-block;
  position: relative;
  vertical-align: middle;

  &__transition {
    width: 100%;
  }

  &--full-height {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 0; // forces the content to the bottom
    height: 100%;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: translateY(-0.5em);
  }

  &-enter-active,
  &-leave-active {
    transition:
      transform var(--animation),
      opacity var(--animation);
  }
}
</style>
